import { Action, createReducer, on } from '@ngrx/store';
import { StartOverStateCleared } from '@store/actions/start-over/start-over.actions';

import {
  acceptAccordion,
  resetAccordionState,
  setExpandedAccordion,
  unacceptAccordion,
} from '../../actions/terms-accordion/terms-accordion.actions';

export enum AccordionId {
  DOCUMENTS_AND_ELECTRONIC_CONSENT = 'documentsAndElectronicConsent',
  BACKUP_WITHHOLDING = 'backupWithholding',
  SIGN_APPLICATION = 'signApplication',
}

export const ACCORDION_ORDER: AccordionId[] = [
  AccordionId.DOCUMENTS_AND_ELECTRONIC_CONSENT,
  AccordionId.BACKUP_WITHHOLDING,
  AccordionId.SIGN_APPLICATION,
];

export interface AccordionState {
  accepted: boolean;
  acceptedAt: string | null;
}

export interface AccordionsMap {
  [AccordionId.DOCUMENTS_AND_ELECTRONIC_CONSENT]: AccordionState;
  [AccordionId.BACKUP_WITHHOLDING]: AccordionState;
  [AccordionId.SIGN_APPLICATION]: AccordionState;
}

export interface TermsAccordionState {
  accordions: AccordionsMap;
  expandedAccordionId: AccordionId | null;
}

export const initialState: TermsAccordionState = {
  accordions: {
    [AccordionId.DOCUMENTS_AND_ELECTRONIC_CONSENT]: { accepted: false, acceptedAt: null },
    [AccordionId.BACKUP_WITHHOLDING]: { accepted: false, acceptedAt: null },
    [AccordionId.SIGN_APPLICATION]: { accepted: false, acceptedAt: null },
  },
  expandedAccordionId: AccordionId.DOCUMENTS_AND_ELECTRONIC_CONSENT,
};

const termsAccordionReducer = createReducer(
  initialState,
  on(acceptAccordion, (state, { accordionId, acceptedAt }) => ({
    ...state,
    accordions: {
      ...state.accordions,
      [accordionId]: { accepted: true, acceptedAt },
    },
  })),
  on(unacceptAccordion, (state, { accordionId }) => ({
    ...state,
    accordions: {
      ...state.accordions,
      [accordionId]: { accepted: false, acceptedAt: null },
    },
  })),
  on(setExpandedAccordion, (state, { accordionId }) => ({
    ...state,
    expandedAccordionId: accordionId,
  })),
  on(resetAccordionState, () => ({
    ...initialState,
  })),
  on(StartOverStateCleared, () => ({ ...initialState })),
);

export function reducer(state: TermsAccordionState | undefined, action: Action) {
  return termsAccordionReducer(state, action);
}

export const getAccordions = (state: TermsAccordionState) =>
  state?.accordions ?? initialState.accordions;

export const getAreAllAccordionsAccepted = (state: TermsAccordionState) => {
  if (!state?.accordions) return false;
  return Object.values(state.accordions).every((accordion) => accordion.accepted);
};

export const getFirstUnacceptedAccordionId = (state: TermsAccordionState): AccordionId | null => {
  if (!state?.accordions) return AccordionId.DOCUMENTS_AND_ELECTRONIC_CONSENT;
  for (const accordionId of ACCORDION_ORDER) {
    if (!state.accordions[accordionId]?.accepted) {
      return accordionId;
    }
  }
  return null;
};

export const canAccordionBeOpened = (
  state: TermsAccordionState,
  accordionId: AccordionId,
): boolean => {
  if (!state?.accordions) return accordionId === AccordionId.DOCUMENTS_AND_ELECTRONIC_CONSENT;
  const accordionIndex = ACCORDION_ORDER.indexOf(accordionId);
  for (let i = 0; i < accordionIndex; i++) {
    if (!state.accordions[ACCORDION_ORDER[i]]?.accepted) {
      return false;
    }
  }
  return true;
};
